import { RoleplaySaveRecovery } from '@/features/roleplay-conversation/session/RoleplaySaveRecovery'
import { reconcileRoleplayAlternatives } from '@/features/roleplay-conversation/session/reconcileAlternatives'
import { retainRoleplaySave, forgetRoleplaySaves, isConfirmedSaveRejection, type PendingRoleplaySave, type RoleplaySaveScope, type RoleplaySaveRetryResult } from '@/features/roleplay-conversation/session/saveRecovery'
import { runRoleplayCommand } from '@/features/roleplay-conversation/session/roleplayCommands'
import { defineStore } from 'pinia'
import { reconcileResponseVariant, startResponse } from '@/features/story-conversation/responseState'
import { createConversationSession, type ConversationOperation, type StoryConversationSession } from '@/features/story-conversation/conversationSession'
import apiService from '@/api'
import { resolveBot } from '@/features/bots/resolveBot'
import { renderConversationMacros } from '@/features/roleplay-conversation/context/promptMacros'
import { normalizeConversationIntros } from '@/lib/conversationIntros'
import {
  createRoleplayMessageOrchestrator,
  MessageSendError,
} from '@/features/roleplay-conversation/generation/RoleplayMessageOrchestrator'
import type { DirectorNoteFromComposer } from '@/features/roleplay-conversation/composer/chatComposerState'
import { useConversationsStore } from '@/stores/conversations'
import { useBotsStore } from '@/features/bots/store'
import type { ConversationContextResponse, MessageDirectorNoteUpdateData, MessageUpdateData } from '@/api/contract'
import type { Bot, Character, Conversation, DirectorNote, Message, MessageAlternative, Persona } from '@/types/domain'
import {
  type MessageSlot,
  type Variant,
  type StoreMessage,
  slotFromMessage,
  getActiveVariant,
  projectSlotsToMessages,
  projectSlotToMessage,
  alternativeVariants,
  compareTimelineEntries,
} from '@/lib/messageSlots'

// Re-exported for the many existing importers of `StoreMessage` from this store.
export type { StoreMessage } from '@/lib/messageSlots'

/** Patch applied to a slot's active variant (plus slot-level director_note). */
type VariantPatch = Partial<Variant> & {
  director_note?: StoreMessage['director_note']
}

export interface RoleplayConversationSessionState {
  conversationId: number | null
  readGeneration: number
  commands: StoryConversationSession
  readiness: 'loading' | 'ready' | 'degraded' | 'error'
  resources: {
    conversation: Conversation | null
    character: Character | null
    persona: Persona | null
    bot: Bot | null
  }
  /**
   * Canonical timeline: one slot per position, each holding N variants. The flat
   * `messages` getter projects this for every reader that only wants the
   * canonical timeline (context building, director notes, sorting, …).
   */
  slots: MessageSlot[]
  loading: boolean
  loadingOlder: boolean
  loadError: string | null
  actionError: string | null
  hasMore: boolean
  oldestLoadedId: number | string | null
  pageSize: number
  pendingSaves: PendingRoleplaySave[]
  recoveryStorageUnavailable: boolean
}

function getErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback
}

// Canonical order is insertion order (numeric `messages.id`); optimistic temp
// slots (string `temp-*` ids) stay pinned to the tail. See compareTimelineEntries.
const sortSlotsChronologically = (slots: MessageSlot[] = []): MessageSlot[] => {
  return [...slots].sort(compareTimelineEntries)
}

const effectiveBot = (conversation: Conversation | null, bots: Bot[]): Bot | null => {
  return resolveBot(bots, conversation?.bot_id, bots.find(bot => bot.is_default) ?? bots[0] ?? null)
}

const readinessFor = (
  resources: RoleplayConversationSessionState['resources'],
): RoleplayConversationSessionState['readiness'] =>
  resources.conversation && resources.character && resources.persona && resources.bot ? 'ready' : 'degraded'

let stopBotSync: (() => void) | null = null

export type SendOutcome =
  | { ok: true }
  | { ok: false; phase: 'busy'; restoreComposer: true }
  | { ok: false; phase: 'user-persist'; restoreComposer: true }
  | { ok: false; phase: 'stream' | 'assistant-persist' | 'user-save-uncertain'; restoreComposer: false }

export interface SendInput {
  text: string
  directorNote?: DirectorNoteFromComposer | null
}

/**
 * Merge freshly-fetched messages into existing slots. Server fields win on
 * `variants[0]`, but client-only phase and any existing alternative variants
 * (+ the active selection) are preserved so a refetch never drops rerolls.
 */
function mergeFetchedIntoSlots(
  existing: MessageSlot[],
  fetched: Message[],
  { replace }: { replace: boolean },
): MessageSlot[] {
  const existingById = new Map(existing.map((slot) => [String(slot.id), slot]))
  const mergedById = new Map<string, MessageSlot>()

  if (!replace) {
    existing.forEach((slot) => mergedById.set(String(slot.id), slot))
  }

  fetched.forEach((msg) => {
    const key = String(msg.id)
    const incoming = slotFromMessage(msg as unknown as StoreMessage)
    const prior = existingById.get(key)

    if (prior) {
      const priorOriginal = prior.variants[0]
      // Server fields win, but keep the client-only phase the store attached.
      incoming.variants[0] = reconcileResponseVariant({
        ...incoming.variants[0],
        phase: priorOriginal?.phase ?? incoming.variants[0].phase,
      }, priorOriginal)
      const alts = alternativeVariants(prior)
      incoming.variants = [incoming.variants[0], ...alts]
      const activeStillPresent = incoming.variants.some(
        (v) => String(v.id) === String(prior.activeVariantId),
      )
      incoming.activeVariantId = activeStillPresent ? prior.activeVariantId : incoming.variants[0].id
    }

    mergedById.set(key, incoming)
  })

  return sortSlotsChronologically([...mergedById.values()])
}

export const useRoleplayConversationSessionStore = defineStore('conversationSession', {
  state: (): RoleplayConversationSessionState => ({
    conversationId: null,
    readGeneration: 0,
    commands: createConversationSession(),
    readiness: 'loading',
    resources: { conversation: null, character: null, persona: null, bot: null },
    slots: [],
    loading: false,
    loadingOlder: false,
    loadError: null,
    actionError: null,
    hasMore: false,
    oldestLoadedId: null,
    pageSize: 50,
    pendingSaves: [],
    recoveryStorageUnavailable: false,
  }),

  getters: {
    saveBlocked: (state): boolean => state.slots.some(slot => Boolean(getActiveVariant(slot).saveState)),
    epoch: (state): number => state.commands.state.epoch,
    sending: (state): boolean => state.commands.busy,
    /**
     * The flat, canonical timeline: each slot projected to its active variant.
     * This is the read surface for everything that is not variant-aware.
     */
    messages(state: RoleplayConversationSessionState): StoreMessage[] {
      return projectSlotsToMessages(state.slots)
    },

    slotById: (state: RoleplayConversationSessionState) => (messageId: number | string) => {
      return state.slots.find(slot => String(slot.id) === String(messageId)) || null
    },

    // Non-original variants (rerolls) for a slot.
    getMessageAlternatives: (state: RoleplayConversationSessionState) => (messageId: number | string): Variant[] => {
      const slot = state.slots.find(s => String(s.id) === String(messageId))
      return slot ? alternativeVariants(slot) : []
    },

    getActiveAlternative: (state: RoleplayConversationSessionState) => (messageId: number | string): Variant | null => {
      const slot = state.slots.find(s => String(s.id) === String(messageId))
      if (!slot) return null
      const active = getActiveVariant(slot)
      return active.origin === 'alternative' ? active : null
    },

    hasAlternatives: (state: RoleplayConversationSessionState) => (messageId: number | string) => {
      const slot = state.slots.find(s => String(s.id) === String(messageId))
      return !!slot && slot.variants.length > 1
    },

    /**
     * Compat projection of the old `alternatives` map (slot id → reroll variants).
     * Kept so the auto-scroll composable can arm on streaming rerolls unchanged.
     */
    alternatives(state: RoleplayConversationSessionState): Record<number | string, Variant[]> {
      const out: Record<number | string, Variant[]> = {}
      state.slots.forEach((slot) => {
        const alts = alternativeVariants(slot)
        if (alts.length) out[slot.id] = alts
      })
      return out
    },

    /** Compat projection of the old `activeAlternatives` map (only when a reroll is active). */
    activeAlternatives(state: RoleplayConversationSessionState): Record<number | string, number | string> {
      const out: Record<number | string, number | string> = {}
      state.slots.forEach((slot) => {
        const active = getActiveVariant(slot)
        if (active.origin === 'alternative') out[slot.id] = active.id
      })
      return out
    },

    isMostRecentAIMessage: (state: RoleplayConversationSessionState) => (messageId: number | string) => {
      if (!state.slots.length) return false

      const aiSlots = state.slots.filter(slot => !slot.is_user_author)
      if (!aiSlots.length) return false

      const mostRecentAISlot = aiSlots.reduce((latest, current) => {
        return compareTimelineEntries(current, latest) > 0 ? current : latest
      })

      return String(mostRecentAISlot.id) === String(messageId)
    }
  },

  actions: {
    stopStreaming() {
      this.commands.stop()
    },

    dispose() {
      // Cancellation precedes the epoch bump so an active stream receives the
      // same abort-empty/abort-partial semantics as an explicit user stop.
      this.stopStreaming()
      stopBotSync?.()
      stopBotSync = null
      this.commands.dispose()
      this.readGeneration++
      this.conversationId = null
      this.pendingSaves = []
      this.recoveryStorageUnavailable = false
      this.resources = { conversation: null, character: null, persona: null, bot: null }
      this.readiness = 'loading'
      this.slots = []
      this.hasMore = false
      this.oldestLoadedId = null
      this.loading = false
      this.loadingOlder = false
      this.loadError = null
      this.actionError = null
    },

    async initialize(conversationId: number) {
      this.dispose()
      this.conversationId = conversationId
      this.loading = true
      const epoch = this.epoch
      const catalogGeneration = useConversationsStore().generation
      try {
        const context: ConversationContextResponse = await apiService.getConversationContext(conversationId)
        if (epoch !== this.epoch) return

        const botsStore = useBotsStore()
        const bots = context.bots.status === 'resolved' ? context.bots.value ?? [] : []
        if (context.bots.status === 'resolved') botsStore.mergeBotsFromContext(bots)
        this.resources = {
          conversation: context.conversation,
          character: context.character.status === 'resolved' ? context.character.value : null,
          persona: context.persona.status === 'resolved' ? context.persona.value : null,
          bot: context.bots.status === 'resolved' ? effectiveBot(context.conversation, bots) : null,
        }
        this.readiness = readinessFor(this.resources)
        this.slots = mergeFetchedIntoSlots([], context.messages, { replace: true })
        this.hasMore = context.messages.length >= this.pageSize
        this.oldestLoadedId = this.slots[0]?.id ?? null
        useConversationsStore().reconcileConversation(context.conversation, catalogGeneration)

        stopBotSync = botsStore.$subscribe(() => {
          if (epoch !== this.epoch) return
          this.resources.bot = effectiveBot(this.resources.conversation, botsStore.bots)
          this.readiness = readinessFor(this.resources)
        }, { detached: true })

        let introSlotEnsured = false
        if (context.messages.length === 0) {
          await this.ensureIntroSlot()
          if (epoch !== this.epoch) return
          introSlotEnsured = true
        }

        const latestAI = [...this.slots].reverse().find((slot) => !slot.is_user_author)
        if (latestAI && !introSlotEnsured) {
          try {
            await this.fetchMessageAlternatives(latestAI.id)
          } catch {
            if (epoch === this.epoch) {
              this.actionError = 'We had trouble loading previous AI alternatives. You can still continue the conversation and reroll responses.'
            }
          }
        }
        await this.restorePendingSaves()
      } catch (error: unknown) {
        if (epoch !== this.epoch) return
        this.loadError = getErrorMessage(error, 'Failed to load conversation')
        this.readiness = 'error'
      } finally {
        if (epoch === this.epoch) this.loading = false
      }
    },

    async setBot(botId: number | null) {
      return runRoleplayCommand(this, 'mutation', async () => {
        if (this.readiness !== 'ready' && this.readiness !== 'degraded') throw new Error('The conversation is not ready to change bots.')
        const conversation = this.resources.conversation
        if (!conversation) throw new Error('The conversation is no longer available.')
        const epoch = this.epoch
        const catalogGeneration = useConversationsStore().generation
        const prior = conversation.bot_id ?? null
        this.actionError = null
        this.resources.conversation = { ...conversation, bot_id: botId }
        this.resources.bot = effectiveBot(this.resources.conversation, useBotsStore().bots)
        this.readiness = readinessFor(this.resources)
        try {
          const response = await apiService.updateConversation(conversation.id, { bot_id: botId })
          if (epoch !== this.epoch) return
          const updated = response.conversation
          this.resources.conversation = updated
          this.resources.bot = effectiveBot(updated, useBotsStore().bots)
          this.readiness = readinessFor(this.resources)
          useConversationsStore().reconcileConversation(updated, catalogGeneration)
        } catch (error: unknown) {
          if (epoch !== this.epoch) return
          this.resources.conversation = { ...conversation, bot_id: prior }
          this.resources.bot = effectiveBot(this.resources.conversation, useBotsStore().bots)
          this.readiness = readinessFor(this.resources)
          this.actionError = getErrorMessage(error, 'Failed to update conversation bot')
          throw error
        }
      })
    },

    async forkConversationAtMessage(messageId: number | string) {
      return runRoleplayCommand(this, 'fork', async () => {
        if (this.readiness !== 'ready') return null
        this.actionError = null
        const epoch = this.epoch
        const catalogGeneration = useConversationsStore().generation

        try {
          const conversation = await apiService.forkConversationAtMessage(messageId)
          if (epoch !== this.epoch) return null
          useConversationsStore().reconcileConversation(conversation, catalogGeneration)
          return conversation
        } catch (error: unknown) {
          if (epoch !== this.epoch) return null
          this.actionError = getErrorMessage(error, 'Failed to fork conversation')
          console.error('Failed to fork conversation:', error)
          throw error
        }
      })
    },

    async ensureIntroSlot() {
      const conversation = this.resources.conversation
      const character = this.resources.character
      const persona = this.resources.persona
      if (!conversation || !character || !persona) return this.messages

      const epoch = this.epoch
      const renderedOpenings = normalizeConversationIntros(
        character.intro_messages,
        character.initial_message,
        character.id,
      ).map((opening) => renderConversationMacros(opening.content, { persona }))

      if (renderedOpenings.length === 0) return this.messages

      await apiService.ensureConversationIntroSlot(conversation.id, renderedOpenings)
      if (epoch !== this.epoch) return this.messages

      await this.fetchMessages(conversation.id, { replace: true })
      if (epoch !== this.epoch) return this.messages

      const latestAI = [...this.slots].reverse().find((slot) => !slot.is_user_author)
      if (latestAI) await this.fetchMessageAlternatives(latestAI.id)
      return this.messages
    },

    async fetchMessages(conversationId: number, options: { limit?: number; replace?: boolean; before_id?: number | string | null } = {}) {
      const epoch = this.epoch
      const readGeneration = this.readGeneration
      const priorLoading = this.loading
      this.loading = true
      this.actionError = null
      const limit = options.limit ?? this.pageSize
      const replace = options.replace ?? false
      try {
        const response = await apiService.getConversationMessages(conversationId, { ...options, limit })
        if (epoch !== this.epoch || readGeneration !== this.readGeneration) return this.messages
        const newMessages = response.messages || []

        this.slots = mergeFetchedIntoSlots(this.slots, newMessages, { replace })
        this.hasMore = limit ? newMessages.length >= limit : false
        this.oldestLoadedId = this.slots[0]?.id || null

        return this.messages
      } catch (error: unknown) {
        if (epoch !== this.epoch || readGeneration !== this.readGeneration) return this.messages
        this.actionError = getErrorMessage(error, 'Failed to fetch messages')
        console.error('Failed to fetch messages:', error)
        throw error
      } finally {
        if (epoch === this.epoch) this.loading = priorLoading
      }
    },

    async loadOlder() {
      const conversationId = this.conversationId
      if (conversationId == null) return this.messages
      if (this.commands.state.command === 'mutation' || this.loadingOlder || !this.hasMore) {
        return this.messages
      }

      const epoch = this.epoch
      const readGeneration = this.readGeneration
      this.loadingOlder = true
      this.actionError = null
      const limit = this.pageSize
      const before_id = this.oldestLoadedId

      try {
        const response = await apiService.getConversationMessages(conversationId, {
          limit,
          before_id
        })
        if (epoch !== this.epoch || readGeneration !== this.readGeneration) return this.messages
        const olderMessages = response.messages || []

        this.slots = mergeFetchedIntoSlots(this.slots, olderMessages, { replace: false })
        this.hasMore = limit ? olderMessages.length >= limit : false
        this.oldestLoadedId = this.slots[0]?.id || null

        return this.messages
      } catch (error: unknown) {
        if (epoch !== this.epoch || readGeneration !== this.readGeneration) return this.messages
        this.actionError = getErrorMessage(error, 'Failed to load older messages')
        console.error('Failed to load older messages:', error)
        throw error
      } finally {
        if (epoch === this.epoch) this.loadingOlder = false
      }
    },

    async runSendMachine(params: {
      messageContent: string
      directorNote?: DirectorNoteFromComposer | null
      skipUserMessage?: boolean
      options?: Record<string, unknown>
    }): Promise<SendOutcome> {
      if (this.sending || this.saveBlocked) return { ok: false, phase: 'busy', restoreComposer: true }
      const { conversation, character, persona, bot } = this.resources
      if (this.readiness !== 'ready' || !conversation?.user_id || !character || !persona || !bot) {
        this.actionError = 'Conversation resources are unavailable.'
        return { ok: false, phase: 'user-persist', restoreComposer: true }
      }

      const operation = this.commands.begin('response')
      if (!operation) return { ok: false, phase: 'busy', restoreComposer: true }
      const epoch = operation.epoch
      this.readGeneration++
      this.actionError = null
      try {
        const scopedStore = scopeRoleplayTimeline(this, operation)
        const orchestrator = createRoleplayMessageOrchestrator({ conversationsStore: scopedStore })
        operation.onStop(() => orchestrator.stopStreaming())
        await orchestrator.sendMessage({
          ...params,
          conversationId: conversation.id,
          character,
          persona,
          messages: this.messages,
          currentBot: bot,
          isCancelled: operation.isCancelled,
        })
        return { ok: true }
      } catch (error: unknown) {
        const sendError = error instanceof MessageSendError
          ? error
          : new MessageSendError('stream', getErrorMessage(error, 'Failed to send message'))
        // Generation errors and unresolved saves have their own timeline UI.
        // A confirmed user-save rejection instead returns to the composer.
        if (epoch === this.epoch && sendError.phase === 'user-persist') {
          this.actionError = sendError.message
        }
        return sendError.phase === 'user-persist'
          ? { ok: false, phase: 'user-persist', restoreComposer: true }
          : { ok: false, phase: sendError.phase, restoreComposer: false }
      } finally {
        if (epoch === this.epoch) this.readGeneration++
        operation.finish()
      }
    },

    async send(input: SendInput): Promise<SendOutcome> {
      return this.runSendMachine({ messageContent: input.text, directorNote: input.directorNote })
    },

    async resend(messageId: number | string): Promise<SendOutcome> {
      const message = this.messages.find(item => String(item.id) === String(messageId))
      if (!message || !message.is_user_author) {
        this.actionError = message ? 'Can only resend user messages' : 'Message not found'
        return { ok: false, phase: 'stream', restoreComposer: false }
      }
      return this.runSendMachine({
        messageContent: message.content,
        skipUserMessage: true,
        options: {
          isResend: true,
          excludeMessageId: messageId,
          sourceMessageId: messageId,
        },
      })
    },

    async reroll(messageId: number | string): Promise<void> {
      if (this.sending || this.saveBlocked) return
      const { conversation, character, persona, bot } = this.resources
      if (this.readiness !== 'ready' || !conversation || !character || !persona || !bot) return
      const operation = this.commands.begin('response')
      if (!operation) return
      const epoch = operation.epoch
      this.readGeneration++
      this.actionError = null
      try {
        const scopedStore = scopeRoleplayTimeline(this, operation)
        const orchestrator = createRoleplayMessageOrchestrator({ conversationsStore: scopedStore })
        operation.onStop(() => orchestrator.stopStreaming())
        await orchestrator.rerollMessage({
          messageId,
          conversationId: conversation.id,
          character,
          persona,
          messages: this.messages,
          currentBot: bot,
          isCancelled: operation.isCancelled,
        })
      } catch (error: unknown) {
        const pendingSave = this.pendingSaves.some(record => String(record.slotId) === String(messageId))
        if (epoch === this.epoch && !pendingSave) this.actionError = getErrorMessage(error, 'Failed to reroll message')
      } finally {
        if (epoch === this.epoch) this.readGeneration++
        operation.finish()
      }
    },

    async updateMessage(id: number | string, messageData: MessageUpdateData) {
      return runRoleplayCommand(this, 'mutation', async () => {
        if (this.readiness !== 'ready') return null
        const epoch = this.epoch
        this.actionError = null
        // Apply the edit locally before the round trip confirms it — a manuscript
        // edit should read as instant. Snapshot the active variant so a failure
        // can restore it exactly, whatever fields messageData touched.
        const slot = this.slots.find(s => String(s.id) === String(id))
        if (slot && getActiveVariant(slot).saveState) return null
        const priorVariant = slot ? { ...getActiveVariant(slot) } : null
        const targetVariantId = priorVariant?.id ?? id
        if (slot) {
          this.updateMessageLocal(id, messageData as VariantPatch)
        }

        try {
          const response = await apiService.updateMessage(targetVariantId, messageData)
          if (epoch !== this.epoch) return null
          const updatedMessage = response?.message ?? response

          if (updatedMessage && slot && priorVariant) {
            this.updateVariant(id, targetVariantId, {
              content: updatedMessage.content,
              phase: updatedMessage.phase ?? priorVariant.phase,
              stream_id: updatedMessage.stream_id,
            })
          }

          return updatedMessage
        } catch (error: unknown) {
          if (epoch !== this.epoch) return null
          if (slot && priorVariant) {
            const index = slot.variants.findIndex(v => String(v.id) === String(priorVariant.id))
            if (index !== -1) slot.variants[index] = priorVariant
          }
          this.actionError = getErrorMessage(error, 'Failed to update message')
          console.error('Failed to update message:', error)
          throw error
        }
      })
    },

    /**
     * Update a slot in the local store without an API call. `director_note` is a
     * slot-level property; everything else patches the slot's active variant.
     */
    updateMessageLocal(id: number | string, messageData: VariantPatch) {
      const slot = this.slots.find(s => String(s.id) === String(id))
      if (!slot) return

      const { director_note, ...variantPatch } = messageData
      if ('director_note' in messageData) {
        slot.director_note = director_note ?? null
      }

      const active = getActiveVariant(slot)
      const index = slot.variants.findIndex(v => String(v.id) === String(active.id))
      if (index !== -1) {
        slot.variants[index] = {
          ...slot.variants[index],
          ...variantPatch,
        }
      }
    },

    async updateMessageDirectorNote(messageId: number | string, noteData: MessageDirectorNoteUpdateData) {
      return runRoleplayCommand(this, 'mutation', async () => {
        if (this.readiness !== 'ready') return null
        const epoch = this.epoch
        this.actionError = null
        // Existing notes can merge optimistically because their server identity
        // and timestamps are already known. New notes wait for the upsert response
        // so the store receives a complete, fully-typed DirectorNote.
        const slot = this.slots.find(s => String(s.id) === String(messageId))
        const priorNote = slot?.director_note ?? null
        if (slot && priorNote) {
          this.updateMessageLocal(messageId, { director_note: { ...priorNote, ...noteData } })
        }

        try {
          const response = await apiService.updateMessageDirectorNote(messageId, noteData)
          if (epoch !== this.epoch) return null
          this.updateMessageLocal(messageId, {
            director_note: response?.director_note ?? null,
          })
          return response?.director_note ?? null
        } catch (error: unknown) {
          if (epoch !== this.epoch) return null
          if (slot) {
            this.updateMessageLocal(messageId, { director_note: priorNote })
          }
          this.actionError = getErrorMessage(error, 'Failed to update director note')
          console.error('Failed to update director note:', error)
          throw error
        }
      })
    },

    async deleteMessageDirectorNote(messageId: number | string) {
      return runRoleplayCommand(this, 'mutation', async () => {
        if (this.readiness !== 'ready') return
        const epoch = this.epoch
        this.actionError = null
        const slot = this.slots.find(s => String(s.id) === String(messageId))
        const priorNote = slot?.director_note ?? null
        if (slot) {
          this.updateMessageLocal(messageId, { director_note: null })
        }

        try {
          await apiService.deleteMessageDirectorNote(messageId)
          if (epoch !== this.epoch) return
        } catch (error: unknown) {
          if (epoch !== this.epoch) return
          if (slot) {
            this.updateMessageLocal(messageId, { director_note: priorNote })
          }
          this.actionError = getErrorMessage(error, 'Failed to delete director note')
          console.error('Failed to delete director note:', error)
          throw error
        }
      })
    },

    /**
     * Remove a slot from the local store without an API call. Used to clean up
     * an errored streaming message (whose variants[0] carries the slot id).
     */
    removeMessageLocal(id: number | string) {
      const index = this.slots.findIndex(slot => String(slot.id) === String(id))
      if (index !== -1) {
        this.slots.splice(index, 1)
      }
    },

    /**
     * Swap a temporary streaming id for the reconciled real id across the slot,
     * its original variant, and the active pointer. Replaces the orchestrator's
     * old direct `messages.splice`, which cannot target a derived projection.
     */
    reconcileMessageId(
      tempId: number | string,
      realId: number | string,
      // The full server row. A streaming slot is built with no timeline fields
      // (`addMessage` has no `t`/`created_at` yet), so this hand-off MUST copy the
      // server's authoritative ones onto the slot — otherwise the reconciled
      // message keeps a real id but no `t`, and any timestamp-based reader mis-
      // orders it. Reconcile is the only point that owns both the slot and the
      // server row, so it is where the hand-off has to be total.
      serverFields?: Partial<Pick<StoreMessage, 't' | 'created_at' | 'updated_at'>> | null,
    ) {
      if (!tempId || !realId || String(tempId) === String(realId)) return
      const slot = this.slots.find(s => String(s.id) === String(tempId))
      if (!slot) return

      slot.client_key = slot.client_key ?? String(tempId)
      if (String(slot.activeVariantId) === String(tempId)) slot.activeVariantId = realId
      slot.variants = slot.variants.map(v =>
        String(v.id) === String(tempId) ? reconcileResponseVariant({ ...v, id: realId }, v) : v,
      )
      slot.id = realId

      if (serverFields) {
        if (serverFields.t != null) slot.t = serverFields.t
        if (serverFields.created_at != null) slot.created_at = serverFields.created_at
        if (serverFields.updated_at != null) slot.updated_at = serverFields.updated_at
      }
    },

    saveScope(): RoleplaySaveScope { return new RoleplaySaveRecovery(this).saveScope() },
    syncPendingSaves(scope: RoleplaySaveScope) { new RoleplaySaveRecovery(this).syncPendingSaves(scope) },
    retainSave(record: PendingRoleplaySave, epoch: number) { new RoleplaySaveRecovery(this).retainSave(record, epoch) },
    saveCompletedMessage(record: PendingRoleplaySave, epoch: number): Promise<number | null> {
      return new RoleplaySaveRecovery(this).saveCompletedMessage(record, epoch)
    },

    async persistStreamedMessage(
      tempId: number | string,
      payload: { conversation_id: number | string | null; content: string; stream_id?: string | null },
      operationEpoch?: number,
      scope?: RoleplaySaveScope,
    ): Promise<number | null> {
      operationEpoch ??= this.epoch
      scope ??= this.saveScope()
      return this.saveCompletedMessage({
        request: { requestId: crypto.randomUUID(), conversationId: scope.conversationId,
          expectedTailId: scope.expectedTailId, content: payload.content, isUserAuthor: false,
          parentMessageId: null, streamId: payload.stream_id ?? null, directorNote: null },
        ownerId: scope.ownerId, slotId: tempId, variantId: tempId, savedMessageId: null,
        createdAt: new Date().toISOString(), active: true, state: 'saving',
      }, operationEpoch)
    },

    async persistUserMessage(
      tempId: number | string,
      payload: { conversation_id: number | string | null; content: string; director_note?: DirectorNoteFromComposer | null; originalText?: string },
      operationEpoch?: number,
      scope?: RoleplaySaveScope,
    ): Promise<number | null> {
      operationEpoch ??= this.epoch
      scope ??= this.saveScope()
      return this.saveCompletedMessage({
        request: { requestId: crypto.randomUUID(), conversationId: scope.conversationId,
          expectedTailId: scope.expectedTailId, content: payload.content, isUserAuthor: true,
          parentMessageId: null, streamId: null, directorNote: payload.director_note ?? null },
        ownerId: scope.ownerId, slotId: tempId, variantId: tempId, savedMessageId: null,
        createdAt: new Date().toISOString(), active: true, state: 'saving', originalText: payload.originalText,
      }, operationEpoch)
    },

    async retryMessageSave(messageId: number | string): Promise<RoleplaySaveRetryResult | null> {
      return runRoleplayCommand(this, 'mutation', async (): Promise<RoleplaySaveRetryResult | null> => {
        const slot = this.slotById(messageId)
        if (!slot) return null
        const requestId = getActiveVariant(slot).saveRequestId
        const record = this.pendingSaves.find(item => item.request.requestId === requestId)
        if (!record) return null
        const epoch = this.epoch
        try {
          const id = await this.saveCompletedMessage(record, epoch)
          return { status: id == null ? 'deleted' : 'saved' }
        }
        catch (error) {
          if (epoch !== this.epoch) return null
          if (record.request.isUserAuthor && isConfirmedSaveRejection(error)) {
            // Keep the original until the view can return it to the composer.
            this.retainSave({ ...record, state: 'pending' }, epoch)
            return { status: 'rejected', submission: record }
          }
          return { status: 'failed' }
        }
      })
    },

    releaseRejectedUserSave(requestId: string) {
      const scope = this.saveScope()
      const record = this.pendingSaves.find(item => item.request.requestId === requestId && item.request.isUserAuthor)
      if (!record) return
      forgetRoleplaySaves(scope, [requestId])
      this.removeMessageLocal(record.slotId)
      this.syncPendingSaves(scope)
    },

    restorePendingSaves(): Promise<void> { return new RoleplaySaveRecovery(this).restorePendingSaves() },

    async deleteMessageAndSubsequent(id: number | string) {
      return runRoleplayCommand(this, 'mutation', async () => {
        if (this.readiness !== 'ready') return null
        const epoch = this.epoch
        this.actionError = null
        try {
          const scope = this.saveScope()
          const slotIndex = this.slots.findIndex(slot => String(slot.id) === String(id))
          if (slotIndex < 0) return null
          const removedSlots = this.slots.slice(slotIndex)
          const cancelled = this.pendingSaves.filter(record => removedSlots.some(slot =>
            String(slot.id) === String(record.slotId) || slot.variants.some(v => v.saveRequestId === record.request.requestId)))
          const target = cancelled.find(record => record.request.parentMessageId == null && String(record.slotId) === String(id))
          const response = await apiService.deleteRoleplayMessages({
            requestId: crypto.randomUUID(), conversationId: scope.conversationId,
            messageId: Number.isFinite(Number(id)) ? Number(id) : null,
            targetRequestId: target?.request.requestId ?? null,
            cancelRequestIds: cancelled.map(record => record.request.requestId),
          })
          forgetRoleplaySaves(scope, cancelled.map(record => record.request.requestId))
          if (epoch !== this.epoch) return null
          this.syncPendingSaves(scope)

          // Refresh messages for the current conversation; the rebuild yields
          // single-variant slots, so any alternatives are cleared implicitly.
          if (this.resources.conversation) {
            await this.fetchMessages(this.resources.conversation.id, { replace: true })
            if (epoch !== this.epoch) return null
            if (this.slots.length === 0) await this.ensureIntroSlot()
          }

          return response
        } catch (error: unknown) {
          if (epoch !== this.epoch) return null
          this.actionError = getErrorMessage(error, 'Failed to delete message and subsequent messages')
          console.error('Failed to delete message and subsequent messages:', error)
          throw error
        }
      })
    },

    addMessage(message: StoreMessage) {
      this.slots.push(slotFromMessage(message))
    },

    clearMessages() {
      this.slots = []
      this.hasMore = false
      this.oldestLoadedId = null
      this.loadingOlder = false
    },

    clearError() {
      this.actionError = null
    },

    // ==================== Variants (reroll) ====================

    async fetchMessageAlternatives(messageId: number | string) {
      const epoch = this.epoch
      const readGeneration = this.readGeneration
      this.actionError = null
      try {
        const response = await apiService.getMessageAlternatives(messageId)
        if (epoch !== this.epoch || readGeneration !== this.readGeneration) return []
        const alternatives = (response.alternatives || []) as MessageAlternative[]

        const slot = this.slots.find(s => String(s.id) === String(messageId))
        if (!slot) return []

        reconcileRoleplayAlternatives(slot, { ...response, alternatives })

        return alternativeVariants(slot)
      } catch (error: unknown) {
        if (epoch !== this.epoch || readGeneration !== this.readGeneration) return []
        this.actionError = getErrorMessage(error, 'Failed to fetch message alternatives')
        console.error('Failed to fetch message alternatives:', error)
        throw error
      }
    },

    /**
     * Begin a client-side reroll: append an empty streaming variant to the slot
     * and make it active. Failed generation removes it; completed prose is
     * retained for recovery if its save fails. Returns the
     * temporary variant id the stream writes into, or null if the slot is gone.
     */
    beginReroll(messageId: number | string): number | string | null {
      const slot = this.slots.find(s => String(s.id) === String(messageId))
      if (!slot) return null
      const tempId = `temp-alt-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
      slot.variants.push(startResponse(tempId, 'alternative'))
      slot.activeVariantId = tempId
      return tempId
    },

    async persistStreamedAlternative(
      parentId: number | string,
      tempVariantId: number | string,
      payload: { content: string; stream_id?: string | null },
      operationEpoch?: number,
      scope?: RoleplaySaveScope,
    ): Promise<number | null> {
      operationEpoch ??= this.epoch
      scope ??= this.saveScope()
      return this.saveCompletedMessage({
        request: { requestId: crypto.randomUUID(), conversationId: scope.conversationId,
          expectedTailId: scope.expectedTailId, content: payload.content, isUserAuthor: false,
          parentMessageId: Number(parentId), streamId: payload.stream_id ?? null, directorNote: null },
        ownerId: scope.ownerId, slotId: parentId, variantId: tempVariantId, savedMessageId: null,
        createdAt: new Date().toISOString(), active: true, state: 'saving',
      }, operationEpoch)
    },

    async selectVariant(messageId: number | string, alternativeId: number | string) {
      const slot = this.slots.find(s => String(s.id) === String(messageId))
      if (!slot || !slot.variants.some(variant => String(variant.id) === String(alternativeId))) return null
      const prior = slot.activeVariantId
      const epoch = this.epoch
      const changed = await runRoleplayCommand(this, 'mutation', async () => {
        this.actionError = null
        slot.activeVariantId = alternativeId
        try {
          const variant = getActiveVariant(slot)
          if (!variant.saveState) await apiService.selectMessageAlternative(messageId, alternativeId)
          if (epoch !== this.epoch) return false
          if (this.pendingSaves.length) {
            const scope = this.saveScope()
            for (const record of this.pendingSaves.filter(item => String(item.slotId) === String(messageId))) {
              retainRoleplaySave({ ...record, active: record.request.requestId === variant.saveRequestId })
            }
            this.syncPendingSaves(scope)
          }
          return true
        } catch (cause) {
          if (epoch !== this.epoch) return false
          slot.activeVariantId = prior
          this.actionError = getErrorMessage(cause, 'Failed to select message alternative')
          return false
        }
      })
      const selected = this.slots.find(s => String(s.id) === String(messageId))
      return changed && selected ? projectSlotToMessage(selected) : null
    },

    /** Append a new variant (a reroll) to a slot and leave selection unchanged. */
    addVariant(messageId: number | string, variant: Variant) {
      const slot = this.slots.find(s => String(s.id) === String(messageId))
      if (!slot) return
      slot.variants.push(variant)
    },

    updateVariant(messageId: number | string, variantId: number | string, updates: VariantPatch) {
      const slot = this.slots.find(s => String(s.id) === String(messageId))
      if (!slot) return
      const index = slot.variants.findIndex(v => String(v.id) === String(variantId))
      if (index !== -1) {
        slot.variants[index] = {
          ...slot.variants[index],
          ...updates,
        }
      }
    },

    /** Remove a variant (errored reroll). Falls back to the original if it was active. */
    removeVariant(messageId: number | string, variantId: number | string) {
      const slot = this.slots.find(s => String(s.id) === String(messageId))
      if (!slot) return
      const index = slot.variants.findIndex(v => String(v.id) === String(variantId))
      if (index > 0) {
        slot.variants.splice(index, 1)
      }
      if (String(slot.activeVariantId) === String(variantId)) {
        slot.activeVariantId = slot.variants[0].id
      }
    },

    /** Drop all rerolls, keeping only the original variant (used by send's prelude). */
    clearMessageAlternatives(messageId: number | string) {
      const slot = this.slots.find(s => String(s.id) === String(messageId))
      if (!slot) return
      slot.variants = [slot.variants[0]]
      slot.activeVariantId = slot.variants[0].id
    },

    /**
     * Collapse a slot to its **active** variant, matching exactly what a reload
     * renders: `getConversationMessages` returns only the anchor row with the
     * active variant's content overlaid (via the `active_variant_id` join), and
     * child variant rows are never fetched. Called on send so a prior AI turn's
     * reroll arrows commit and disappear in-memory instead of lingering until the
     * next refetch. The selection is already persisted (active_variant_id), so
     * this is a pure client-side collapse — nothing to write.
     *
     * Note this collapses to the *active* variant, not `variants[0]`
     * (clearMessageAlternatives' behavior), so a committed reroll keeps the
     * content the user actually chose.
     */
    flattenToActiveVariant(messageId: number | string) {
      const slot = this.slots.find(s => String(s.id) === String(messageId))
      if (!slot) return
      const active = getActiveVariant(slot)
      if (active.saveState) return
      const discarded = this.pendingSaves.filter(record => String(record.slotId) === String(messageId))
      if (discarded.length) {
        const scope = this.saveScope()
        forgetRoleplaySaves(scope, discarded.map(record => record.request.requestId))
        this.syncPendingSaves(scope)
      }
      if (slot.variants.length <= 1) return
      slot.variants = [{
        ...active,
        // Reload gives the single surviving variant the anchor's id and origin.
        id: slot.id,
        origin: 'message',
        reasoning_content: null,
      }]
      slot.activeVariantId = slot.id
    }
  }
})

export type RoleplayConversationSessionContract = Pick<ReturnType<typeof useRoleplayConversationSessionStore>,
  | 'messages'
  | 'addMessage'
  | 'updateMessageLocal'
  | 'removeMessageLocal'
  | 'clearMessageAlternatives'
  | 'fetchMessages'
  | 'beginReroll'
  | 'persistStreamedAlternative'
  | 'persistStreamedMessage'
  | 'persistUserMessage'
  | 'flattenToActiveVariant'
  | 'updateVariant'
  | 'removeVariant'
  | 'reconcileMessageId'
>

/** Explicit session boundary: stale callbacks cannot mutate a replacement timeline. */
function scopeRoleplayTimeline(store: ReturnType<typeof useRoleplayConversationSessionStore>, operation: ConversationOperation): RoleplayConversationSessionContract {
  const epoch = operation.epoch
  const current = operation.isCurrent
  const scope = store.saveScope()
  return {
    get messages() { return current() ? store.messages : [] },
    addMessage: (...args) => { if (current()) store.addMessage(...args) },
    updateMessageLocal: (...args) => { if (current()) store.updateMessageLocal(...args) },
    removeMessageLocal: (...args) => { if (current()) store.removeMessageLocal(...args) },
    clearMessageAlternatives: (...args) => { if (current()) store.clearMessageAlternatives(...args) },
    fetchMessages: (...args) => current() ? store.fetchMessages(...args) : Promise.resolve([]),
    beginReroll: (...args) => current() ? store.beginReroll(...args) : null,
    // A durable save may finish after navigation; only its local projection is fenced.
    persistStreamedMessage: (id, payload) => store.persistStreamedMessage(id, payload, epoch, scope),
    persistStreamedAlternative: (id, variant, payload) => store.persistStreamedAlternative(id, variant, payload, epoch, scope),
    persistUserMessage: async (id, payload) => {
      if (!current()) return null
      const realId = await store.persistUserMessage(id, payload, epoch, scope)
      if (realId != null) scope.expectedTailId = realId
      return realId
    },
    flattenToActiveVariant: (...args) => { if (current()) store.flattenToActiveVariant(...args) },
    updateVariant: (...args) => { if (current()) store.updateVariant(...args) },
    removeVariant: (...args) => { if (current()) store.removeVariant(...args) },
    reconcileMessageId: (...args) => { if (current()) store.reconcileMessageId(...args) },
  }
}
